import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface PaloSort {
    key: string;
    order: 'asc' | 'desc';
}

/**
 * The sorting a list shows, and the parameters it asks the server with.
 *
 * Every list drew its default sorting into the column header but sent the first
 * request without one, and the server then falls back to ascending. So a list
 * opened with the arrow on "created, newest first" and the oldest record on top
 * — the header said one thing and the rows did another (#1451).
 *
 * The request now carries whatever the header shows.
 */
export const usePaloSort = (
    defaultKey: string,
    defaultOrder: 'asc' | 'desc' = 'desc'
): { sort: PaloSort; params: URLSearchParams } => {
    const [searchParams] = useSearchParams();
    const search = searchParams.toString();
    const key = searchParams.get('sort') ?? defaultKey;
    const order = (searchParams.get('order') as 'asc' | 'desc' | null) ?? defaultOrder;

    // The lists fetch on this object, so it may only change when the sorting
    // does — a fresh one on every render would fetch on every render.
    const params = useMemo(() => {
        const next = new URLSearchParams(search);

        next.set('sort', key);
        next.set('order', order);

        return next;
    }, [search, key, order]);

    return { sort: { key, order }, params };
};
